import React, { useMemo } from "react";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@jsq/ds/core";
import {
  deriveIssueColumns,
  formatIssueCell,
  humanizeColumnKey,
  type IssueRow,
} from "#utils/checkDetail.utils";
import * as styles from "./styles_vanilla.css";

export type CheckIssuesTableProps = {
  issues: IssueRow[];
};

/**
 * Per-row diagnostics for a VISUAL check, rendered schema-opaquely: columns are
 * derived from the row keys (routing/discriminator keys already stripped at the
 * view-model boundary), matching the predecessor UI's issues presentation.
 */
export const CheckIssuesTable: React.FC<CheckIssuesTableProps> = ({ issues }) => {
  const columns = useMemo(() => deriveIssueColumns(issues), [issues]);
  // Diagnostic rows have no natural id; assign a positional key here rather
  // than keying on the map index at the JSX site.
  const rows = useMemo(
    () => issues.map((cells, index) => ({ key: `issue-${index}`, cells })),
    [issues],
  );

  // After the hooks, so the early return can't change how many run.
  if (columns.length === 0) {
    return null;
  }

  return (
    <Table condensed className={styles.detailTable}>
      <TableHeader>
        <TableRow>
          {columns.map((column) => (
            <TableCell key={column} header nowrap>
              {humanizeColumnKey(column)}
            </TableCell>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key} alignment="top">
            {columns.map((column) => (
              <TableCell key={column}>{formatIssueCell(row.cells[column] ?? null)}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
